import React, { useMemo } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { OnSearchFunc } from '@scentregroup/shared/hub-components/searchable-faceted-filter'
import { IFacetSelections, IFacet } from '../../types/facets'
import { ICategory } from '../../types/marketplace-categories'
import { scrollToTop } from '../../lib/scroll-to-top'
import { IBreadcrumb } from '../../components/breadcrumbs'
import {
  DropdownOption,
  DROPDOWN_OPTIONS,
  SortOption,
} from '../../components/sort-options'
import { CookieName, useCookie } from '../../lib/cookie'
import { IMarketplaceProductsSearch } from '../../graphql/queries/marketplace-products-search'
import {
  createUrlSearchString,
  getTransferableUrlSearchParams,
} from './url-util'
import ProductsSimplified, { IBlurbProps } from './products-simplified'
import { calculatePagination, makeProductIndexBreadcrumbs } from './page-helper'
import {
  useFacetOptionSearch,
  makeProductIndexEventHandlers,
} from './event-handlers'
import { useWWOOBanner } from '@scentregroup/shared/hooks/use-wwoo-banner'
import { IFooterLinkProps } from './footer-links'
import omit from 'lodash/omit'

export interface IProductsIndexState {
  query: string
  page: number
  sort: SortOption
  itemsPerPage: number
  onSale: boolean
  facetSelections: IFacetSelections
  productTypes: string[]
}

export interface ICategoryDataProps {
  category: ICategory
  productFacets: IFacet[] | undefined
}

export interface IHeadingProps {
  title: string
  description: string
  heading: string
  breadcrumbTitle?: string | undefined
}

export interface IHeroImageSet {
  masterSupportingColour: string | null
  master: { url: string }
}

export interface IBannerProps {
  title: string
  shortDescription: string
  heroImageSet: IHeroImageSet | null
  termsAndConditions?: string
  bodyCopy: string
}

interface IProductsIndexProps {
  header: IHeadingProps
  categoryData: ICategoryDataProps
  productsSearch: IMarketplaceProductsSearch
  productsIndexStateDefaults: IProductsIndexState
  productsIndexState: IProductsIndexState
  isLoadingProducts?: boolean
  isLoadingCategory?: boolean
  basePath: string
  otherFilters?: any
  sortByOptions?: DropdownOption[]
  allowAutoSurface?: boolean
}

interface IBrandProductIndexProps extends IProductsIndexProps {
  pageType: 'BRAND'
  brandName: string
  banner?: never
  blurb?: never
  footerLinks?: never
  customBreadcrumbs?: never
}

interface ICollectionProductIndexProps extends IProductsIndexProps {
  pageType: 'COLLECTION'
  banner: Omit<IBannerProps, 'heroImageSet'> & {
    heroImageSet: IHeroImageSet
  }
  blurb?: IBlurbProps
  footerLinks: IFooterLinkProps[]
  customBreadcrumbs?: never
}

interface IProductsProductIndexProps extends IProductsIndexProps {
  pageType: 'PRODUCTS'
  banner?: never
  blurb?: IBlurbProps
  footerLinks: IFooterLinkProps[]
  customBreadcrumbs?: never
}

interface ICannedSearchProductIndexProps extends IProductsIndexProps {
  pageType: 'CANNED_SEARCH'
  banner?: never
  blurb?: IBlurbProps
  footerLinks: IFooterLinkProps[]
  customBreadcrumbs: IBreadcrumb[]
}

interface IPromotionProductIndexProps extends IProductsIndexProps {
  pageType: 'PROMOTION'
  blurb: IBlurbProps
  banner: Omit<
    IBannerProps,
    'heroImageSet' | 'termsAndConditions' | 'shortDescription'
  > & {
    heroImageSet: IHeroImageSet | null
    termsAndConditions: string
    shortDescription: string | null
  }
  footerLinks?: never
  customBreadcrumbs?: never
}

export type ProductsIndexProps =
  | IBrandProductIndexProps
  | IPromotionProductIndexProps
  | ICollectionProductIndexProps
  | IProductsProductIndexProps
  | ICannedSearchProductIndexProps

export const PRODUCTS_INDEX_CANONICAL_PARAMS = [
  'brand',
  'colour',
  'on-sale',
  'page',
  'q',
  'store',
]

const ProductsIndex: React.FunctionComponent<
  React.PropsWithChildren<ProductsIndexProps>
> = props => {
  useWWOOBanner()
  const {
    children: _children,
    categoryData,
    isLoadingCategory,
    productsSearch,
    isLoadingProducts,
    productsIndexStateDefaults,
    productsIndexState,
    basePath,
    header,
    otherFilters,
    sortByOptions = DROPDOWN_OPTIONS,
    allowAutoSurface,
    ...restProps
  } = props

  let productList: string

  switch (restProps.pageType) {
    case 'BRAND': {
      productList = `Category List Brand (${categoryData.category.title} for ${restProps.brandName})`
      break
    }
    case 'COLLECTION': {
      productList = `Collection (${restProps.banner.title})`
      break
    }
    case 'PRODUCTS': {
      if (categoryData.category) {
        productList = `Category List (${categoryData.category.title})`
      } else {
        productList = 'Category List (show all)'
      }
      break
    }
    case 'PROMOTION': {
      productList = `Promotion (${restProps.banner.title})`
      break
    }
    case 'CANNED_SEARCH': {
      productList = 'Canned Search'
      break
    }
  }

  const history = useHistory()
  const location = useLocation()
  const [, setItemsPerPagePreference] = useCookie(CookieName.PER_PAGE)
  const numberOfProducts = productsSearch.count ?? 0
  const products = productsSearch.results ?? []
  const productsListAnalytics = {
    productList,
    queryID: productsSearch.queryID,
    indexName: productsSearch.indexName,
  }

  const { start, end, numberOfPages } = calculatePagination(
    productsIndexState.page,
    productsIndexState.itemsPerPage,
    numberOfProducts
  )

  const urlSearchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )
  const facets = useMemo(
    () => categoryData.productFacets ?? [],
    [categoryData.productFacets]
  )
  const breadcrumbSearchParams = useMemo(
    () => getTransferableUrlSearchParams([], urlSearchParams),
    [urlSearchParams]
  )
  const categoryTreeSearchParams = useMemo(
    () =>
      getTransferableUrlSearchParams(
        facets?.map(f => f.id) ?? [],
        urlSearchParams
      ),
    [facets, urlSearchParams]
  )
  const breadcrumbs = useMemo((): IBreadcrumb[] => {
    if (restProps.customBreadcrumbs) {
      return restProps.customBreadcrumbs
    }
    return makeProductIndexBreadcrumbs(
      basePath,
      breadcrumbSearchParams,
      productsIndexState.query,
      header.breadcrumbTitle,
      categoryData.category.path,
      allowAutoSurface
    )
  }, [
    header.breadcrumbTitle,
    productsIndexState.query,
    basePath,
    categoryData.category?.path,
    breadcrumbSearchParams,
    allowAutoSurface,
    restProps.customBreadcrumbs,
  ])

  const updatedIndexState = (
    update: Partial<IProductsIndexState>
  ): IProductsIndexState => {
    const withUpdate = {
      ...productsIndexStateDefaults,
      ...productsIndexState,
      ...update,
    }
    // Omit defaults in the URL for SEO:
    ;['itemsPerPage', 'sort'].forEach(key => {
      if (withUpdate[key] === productsIndexStateDefaults[key]) {
        delete withUpdate[key]
      }
    })
    return withUpdate
  }

  const calculateRelativeUrlForUpdate = (
    update: Partial<IProductsIndexState>
  ): string => {
    const state = updatedIndexState(update)
    // Employ a relative path in the link URL:
    return `${location.pathname}?${createUrlSearchString(
      props.pageType === 'CANNED_SEARCH' ? omit(state, 'query') : state
    )}`
  }

  const makePageLinkForPagination = (page: number): string =>
    calculateRelativeUrlForUpdate({ page })

  const updateUrlSearchString = (
    update: Partial<IProductsIndexState>,
    enableScrollToTop = true
  ): void => {
    const state = updatedIndexState(update)
    const urlSearchString = createUrlSearchString(
      props.pageType === 'CANNED_SEARCH' ? omit(state, 'query') : state
    )
    // Employ a relative path in the link URL:
    // pathname needed 'coz only providing search string resets the path to the original one that Next created
    const { pathname } = location

    if (state.page != productsIndexState.page) {
      history.push({
        pathname,
        search: urlSearchString,
      })
    } else {
      history.replace({
        pathname,
        search: urlSearchString,
      })
    }
    enableScrollToTop && scrollToTop()
  }

  const {
    onChangeFacetSelections,
    onChangeProductTypeSelections,
    onClearAll,
    onChangeSort,
    onChangeOnSale,
    onChangeSortOption,
    onClickSortOption,
    onChangeItemsPerPage,
    onClickItemsPerPage,
    onClickPaginationLink: onClickPaginationLinkEventHandler,
  } = makeProductIndexEventHandlers(
    updateUrlSearchString,
    setItemsPerPagePreference
  )

  const onClickPaginationLink = (page: number): void => {
    onClickPaginationLinkEventHandler(page, productsIndexState.itemsPerPage)
  }

  const onFacetOptionSearch: OnSearchFunc = useFacetOptionSearch(
    productsIndexState,
    otherFilters,
    categoryData.category.path
  )

  return (
    <ProductsSimplified
      {...restProps}
      categoryData={categoryData}
      header={header}
      breadcrumbs={breadcrumbs}
      isLoading={{ products: isLoadingProducts, category: isLoadingCategory }}
      result={{ start, end, numberOfProducts }}
      facets={facets}
      productsIndexState={productsIndexState}
      facetfilter={{
        onChangeFacetSelections,
        onChangeProductTypeSelections,
        onClearAll,
        onChangeSort,
        onChangeOnSale,
        onFacetOptionSearch,
      }}
      categoryTreeSearchParams={categoryTreeSearchParams}
      basePath={basePath}
      sortOptions={{ onChangeSortOption, onClickSortOption, sortByOptions }}
      products={products}
      productsListAnalytics={productsListAnalytics}
      dropdownOptions={{ onChangeItemsPerPage, onClickItemsPerPage }}
      pagination={{
        numberOfPages,
        onClickPaginationLink,
        makePageLinkForPagination,
      }}
    />
  )
}

export default ProductsIndex
